import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Alert,
    Container,
    Breadcrumb,
    Spinner
} from 'react-bootstrap';
import { FiUpload, FiEye, FiLoader, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { useNavigate, useParams, NavLink } from "react-router-dom";
import { getStatusDisplay } from './EBRCHelper';
import axiosInstance from '../../../../../../services/instance';
import Base from '../../../../../common/Base';
import PaginationComponent from '../../../../../../constants/PaginationComponent';

const PeriodCreatedList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage] = useState(25);
    const [uploadData, setUploadData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Navigation & Params
    const navigate = useNavigate();

    // Data transformation functions
    const formatDate = (dateString) => {
        if (!dateString || dateString === null) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN');
        } catch (error) {
            return '-';
        }
    };

    const formatDisplayValue = (value, fallback = '-') => {
        if (value === null || value === undefined || value === '') return fallback;
        return value;
    };

    // API Functions
    const fetchUploadStatus = async (page = 1, showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        setError('');

        try {
            // Make actual API call
            const res = await axiosInstance.post(`/api/v1/ebrc/client/irm/period/list`, {
                page: page,
                perpage: perPage,
            });

            if (res.data && res.data.type === 'success') {
                const responseData = res.data.data;
                setUploadData(responseData.lists || []);
                setTotalCount(responseData.total_count || 0);
                setCurrentPage(responseData.page || page);

                if ((responseData.lists?.length === 0 || !responseData.lists) && page === 1) {
                    setSuccess('No IRM Period records');
                }
            } else {
                throw new Error(res.data?.message || 'Failed to fetch IRM period');
            }

        } catch (err) {
            console.error('Fetch IRM period Status Error:', err);

            // Handle different error scenarios
            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    setError('E-BRC not found');
                } else if (status === 403) {
                    setError('Access denied. You don\'t have permission to view the status.');
                } else if (status >= 500) {
                    setError('Server error occurred. Please try again later.');
                } else {
                    setError(data?.message || 'Failed to fetch status. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to fetch the  status. Please try again.');
            }

            setUploadData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const handlePagination = async (page) => {
        setCurrentPage(page);
        await fetchUploadStatus(page);
        // Scroll to top after data is loaded
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    // Load initial data
    useEffect(() => {
        fetchUploadStatus(1);
    }, []);

    // Auto-clear success message
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const handleReport = async (item, actionType) => {
        setActionLoading(true);
        setError('');
        try {
            let route;
            switch (actionType) {
                case 'view':
                    route = `/gst-forms/irm/details/${item.id}`;
                    break;
                default:
                    route = `/gst-forms/irm/details/${item.id}`;
            }

            // Simulate navigation delay
            await new Promise(resolve => setTimeout(resolve, 500));
            navigate(route);

        } catch (err) {
            console.error('Navigation Error:', err);
            setError('Failed to navigate to report. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const statusConfig = {
        1: {
            label: "View IRM",
            variant: "primary",
            icon: <FiEye className="me-1" />,
            actionType: "view",
            disabled: false
        },
        2: {
            label: "Processing",
            variant: "primary",
            icon: <FiLoader className="me-1 spin" />,
            actionType: "processing",
            disabled: true
        },
        3: {
            label: "Error",
            variant: "danger",
            icon: <FiDownload className="me-1" />,
            actionType: "error",
            disabled: false
        },
    };


    const renderActionButton = (item) => {
        const config = statusConfig[item.status];

        return (
            <Button
                variant={config.variant}
                size="sm"
                className="d-inline-flex align-items-center no-hover"
                onClick={() => handleReport(item, config.actionType)}
                disabled={actionLoading || config.disabled}
            >
                {config.icon}
                {config.label}
            </Button>
        );
    };

    return (
        <Base header="EBRC" headerTitle={'ebrc'}>
            <Container fluid className="p-4 bg-light min-vh-100">
                <div>
                    <Row className="align-items-center justify-content-between mb-4">
                        <Col>
                            <h4 className="fw-bold mb-0">e-BRC</h4>
                            <p className="text-muted mb-0">Review and manage e-BRC.</p>
                        </Col>
                    </Row>

                    {/* Success Alert */}
                    {success && (
                        <Alert variant="success" dismissible onClose={clearMessages} className="mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert variant="danger" dismissible onClose={clearMessages} className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {/* Main Content Card */}
                    <Card className="border-0">
                        <Card.Body>
                            {initialLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <p className="text-muted">Loading E-BRC...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="core-ui-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="text-left" scope="col">USER NAME</th>
                                                    <th className="text-left" scope="col">Start Date</th>
                                                    <th className="text-left" scope="col">End Date</th>
                                                    <th className="text-left" scope="col">Created DATE</th>
                                                    <th className="text-left" scope="col">UPLOAD STATUS</th>
                                                    <th className='text-left' scope="col">ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && uploadData.length > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-2">
                                                            <small className="text-muted">
                                                                <Spinner animation="border" size="sm" className="me-2" />
                                                                Updating records...
                                                            </small>
                                                        </td>
                                                    </tr>
                                                )}

                                                {uploadData.length > 0 ? (
                                                    uploadData.map((item) => (
                                                        <tr key={item.id}>
                                                            <td className="fw-medium text-left">
                                                                {formatDisplayValue(item.userName)}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDisplayValue(item.start_date)}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDisplayValue(item.end_date)}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDate(item.created_date)}
                                                            </td>
                                                            <td className='font-bold text-left'>
                                                                {getStatusDisplay(item.status)}
                                                            </td>
                                                            <td className="text-left">
                                                                {renderActionButton(item)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-muted">
                                                            {error ? 'Unable to load data' : 'No data found'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {totalCount > perPage && (
                                        <div className="mt-3">
                                            <PaginationComponent
                                                total_count={totalCount}
                                                current_page={currentPage}
                                                handleOnClick={handlePagination}
                                                per_page={perPage}
                                            />
                                        </div>
                                    )}

                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </Container>
        </Base>
    );
};

export default PeriodCreatedList;